import React from 'react';
import CustomReportService from '../../../services/reports/CustomReport.service';
import { SaintBernardExportRaw } from '../../../interfaces/reports/SaintBernardExport.interface';
import EpochMonthService from '../../../services/EpochMonth.service';
import ReportService from '../../../services/reports/Report.service';
import * as XLSX from 'xlsx';

interface Props {
    clientId: string | number;
}

type SortKey = 'classCode' | 'className' | 'month' | 'receivedCost' | 'inflowPlanRetail' | 'inflowPlanCost' | 'salesPlan' | 'onOrderCost';

type Group = {
    classCode: string;
    className: string;
    totals: {
        receivedCost: number;
        inflowPlanRetail: number;
        inflowPlanCost: number;
        salesPlan: number;
        onOrderCost: number;
    };
    months: SaintBernardExportRaw[]; // sorted by epochMonthSpan asc by default
    expanded: boolean;
}

const SaintBernardExport: React.FC<Props> = ({ clientId }) => {
    const [loading, setLoading] = React.useState(false);
    const [groups, setGroups] = React.useState<Group[]>([]);
    const [sortKey, setSortKey] = React.useState<SortKey>('classCode');
    const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('asc');

    const rawRef = React.useRef<SaintBernardExportRaw[]>([]);

    // Use a stable key for groups to avoid index-based state bugs after sorting
    const getGroupKey = React.useCallback((classCode: string, className: string) => `${classCode}||${className}`,[ ]);

    React.useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const data = await CustomReportService.getSaintBernardExportData(clientId);
                rawRef.current = data || [];
                const byClass = new Map<string, Group>();
                for (const row of rawRef.current) {
                    const key = `${row.classCode}||${row.className}`;
                    if (!byClass.has(key)) {
                        byClass.set(key, {
                            classCode: row.classCode,
                            className: row.className,
                            totals: { receivedCost: 0, inflowPlanRetail: 0, inflowPlanCost: 0, salesPlan: 0, onOrderCost: 0 },
                            months: [],
                            expanded: false,
                        });
                    }
                    const g = byClass.get(key)!;
                    g.months.push(row);
                    g.totals.receivedCost += row.receivedCost || 0;
                    g.totals.inflowPlanRetail += row.inflowPlanRetail || 0;
                    g.totals.inflowPlanCost += row.inflowPlanCost || 0;
                    g.totals.salesPlan += row.salesPlan || 0;
                    g.totals.onOrderCost += row.onOrderCost || 0;
                }
                const sortedGroups = Array.from(byClass.values()).map(g => ({
                    ...g,
                    months: g.months.sort((a, b) => a.epochMonthSpan - b.epochMonthSpan),
                }));
                setGroups(sortedGroups);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [clientId]);

    function toggleExpand(key: string) {
        setGroups(prev => prev.map(g => getGroupKey(g.classCode, g.className) === key ? { ...g, expanded: !g.expanded } : g));
    }

    function onSort(key: SortKey) {
        setSortKey(prevKey => {
            const nextDir = prevKey === key ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc';
            setSortDir(nextDir);
            return key;
        });
    }

    const sortedView = React.useMemo(() => {
        const copy = groups.slice();
        copy.sort((a, b) => {
            const dir = sortDir === 'asc' ? 1 : -1;
            switch (sortKey) {
                case 'classCode':
                    return dir * String(a.classCode).localeCompare(String(b.classCode), undefined, { numeric: true });
                case 'className':
                    return dir * String(a.className).localeCompare(String(b.className));
                case 'receivedCost':
                    return dir * (a.totals.receivedCost - b.totals.receivedCost);
                case 'inflowPlanRetail':
                    return dir * (a.totals.inflowPlanRetail - b.totals.inflowPlanRetail);
                case 'inflowPlanCost':
                    return dir * (a.totals.inflowPlanCost - b.totals.inflowPlanCost);
                case 'salesPlan':
                    return dir * (a.totals.salesPlan - b.totals.salesPlan);
                case 'onOrderCost':
                    return dir * (a.totals.onOrderCost - b.totals.onOrderCost);
                case 'month':
                    // sort by first month in the group
                    const am = a.months.length ? a.months[0].epochMonthSpan : 0;
                    const bm = b.months.length ? b.months[0].epochMonthSpan : 0;
                    return dir * (am - bm);
                default:
                    return 0;
            }
        });
        return copy;
    }, [groups, sortKey, sortDir]);

    const exportToExcel = () => {
        const raw = rawRef.current || [];
        if (!raw.length) return;

        // sort raw by classCode asc, epochMonthSpan asc
        const sorted = raw.sort((a, b) => {
            const cc = String(a.classCode).localeCompare(String(b.classCode), undefined, { numeric: true });
            if (cc !== 0) return cc;
            return a.epochMonthSpan - b.epochMonthSpan;
        });

        const columns = [
            'sort','classcode','classname','epochMonth','parentclientclasscode','parentlocationclassname','receivedcost','salesplan','inflowplanretail','inflowplancost','onordercost'
        ];
        // Sort is concat of classCode className epochMonthSpan (no space)
        const excelData = sorted.map(r => [
            `${r.classCode}${r.className}${r.epochMonthSpan}`,
            r.classCode,
            r.className,
            r.epochMonthSpan,
            r.parentClientClassCode,
            r.parentClientClassName,
            r.receivedCost,
            r.salesPlan,
            r.inflowPlanRetail,
            r.inflowPlanCost,
            r.onOrderCost, 
        ]);
        const ws = XLSX.utils.aoa_to_sheet([columns, ...excelData]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'SaintBernard');
        XLSX.writeFile(wb, `Saint_Bernard_Export_${new Date().toISOString().slice(0,10)}.xlsx`);
    };

    const HeaderCell: React.FC<{label: string; sk: SortKey}> = ({ label, sk }) => (
        <th
            className="px-3 py-2 text-left cursor-pointer select-none"
            onClick={() => onSort(sk)}
            title="Click to sort"
        >
            <div className="flex items-center space-x-1">
                <span>{label}</span>
                {sortKey === sk && (
                    <span className="text-xs">{sortDir === 'asc' ? '▲' : '▼'}</span>
                )}
            </div>
        </th>
    );

    return (
        <div className="p-4">
            <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold">Saint Bernard Export</h2>
                <button onClick={exportToExcel} className="h-9 px-4 rounded-md text-sm font-semibold text-white bg-tw-primary hover:bg-sky-600 shadow">Download Excel</button>
            </div>
            {loading ? (
                <div>Loading...</div>
            ) : (
                <div className="overflow-auto border rounded">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-100">
                            <tr>
                                <HeaderCell label="Class Code" sk="classCode" />
                                <HeaderCell label="Class Name" sk="className" />
                                <HeaderCell label="Month" sk="month" />
                                <HeaderCell label="Received Cost" sk="receivedCost" />
                                <HeaderCell label="Sales Plan" sk="salesPlan" />
                                <HeaderCell label="Inflow Plan Retail" sk="inflowPlanRetail" />
                                <HeaderCell label="Inflow Plan Cost" sk="inflowPlanCost" />
                                <HeaderCell label="On Order Cost" sk="onOrderCost" />
                            </tr>
                        </thead>
                        <tbody>
                            {sortedView.map((g) => (
                                <React.Fragment key={`${g.classCode}-${g.className}`}>
                                    <tr className="hover:bg-gray-50">
                                        <td className="px-3 py-2">
                                            <button
                                                onClick={() => toggleExpand(getGroupKey(g.classCode, g.className))}
                                                className="mr-2 text-xs bg-gray-200 hover:bg-gray-300 rounded px-2 py-0.5"
                                            >{g.expanded ? '−' : '+'}</button>
                                            {g.classCode}
                                        </td>
                                        <td className="px-3 py-2">{g.className}</td>
                                        <td className="px-3 py-2 font-semibold">TOTAL</td>
                                        <td className="px-3 py-2">{ReportService.formatCurrency(g.totals.receivedCost)}</td>
                                        <td className="px-3 py-2">{ReportService.formatCurrency(g.totals.salesPlan)}</td>
                                        <td className="px-3 py-2">{ReportService.formatCurrency(g.totals.inflowPlanRetail)}</td>
                                        <td className="px-3 py-2">{ReportService.formatCurrency(g.totals.inflowPlanCost)}</td>
                                        <td className="px-3 py-2">{ReportService.formatCurrency(g.totals.onOrderCost)}</td>
                                    </tr>
                                    {g.expanded && g.months.map((m) => (
                                        <tr key={`${m.classCode}-${m.epochMonthSpan}`} className="bg-white hover:bg-gray-50">
                                            <td className="px-3 py-2"></td>
                                            <td className="px-3 py-2"></td>
                                            <td className="px-3 py-2">{EpochMonthService.epochMonthToMMMYY(m.epochMonthSpan)}</td>
                                            <td className="px-3 py-2">{ReportService.formatCurrency(m.receivedCost)}</td>
                                            <td className="px-3 py-2">{ReportService.formatCurrency(m.salesPlan)}</td>
                                            <td className="px-3 py-2">{ReportService.formatCurrency(m.inflowPlanRetail)}</td>
                                            <td className="px-3 py-2">{ReportService.formatCurrency(m.inflowPlanCost)}</td>
                                            <td className="px-3 py-2">{ReportService.formatCurrency(m.onOrderCost)}</td>
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default SaintBernardExport;
